import React, { useState, useEffect } from 'react';
import { FaTimesCircle, FaPlus, FaUndo, FaClock, FaFilter } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';
import UserSearch from '../components/UserSearch';

const AdminWarnings = () => {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter & Pagination
  const [filter, setFilter] = useState({ type: '', active: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal state
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newWarning, setNewWarning] = useState({
    type: 'yellow',
    reason: '',
    expiresAt: ''
  });

  useEffect(() => {
    fetchWarnings();
  }, [currentPage, filter]);

  const fetchWarnings = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...filter
      });
      
      const response = await axiosInstance.get(`/admin/warnings?${queryParams}`);
      setWarnings(response.data.warnings);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      console.error('Fehler beim Laden der Warnungen:', err);
      setError('Warnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const issueWarning = async () => {
    try {
      if (!selectedUser || !newWarning.reason.trim()) {
        alert('Bitte User und Grund auswählen');
        return;
      }

      const warningData = {
        userId: selectedUser.id,
        type: newWarning.type,
        reason: newWarning.reason,
        expiresAt: newWarning.expiresAt || null
      };

      await axiosInstance.post('/admin/warnings/issue', warningData);
      
      setShowIssueModal(false);
      setSelectedUser(null);
      setNewWarning({ type: 'yellow', reason: '', expiresAt: '' });
      fetchWarnings();
      
      alert('Warnung erfolgreich vergeben');
    } catch (err) {
      console.error('Fehler beim Vergeben der Warnung:', err);
      alert('Fehler beim Vergeben der Warnung');
    }
  };

  const revokeWarning = async (warningId) => {
    setConfirmAction(() => async () => {
      try {
        await axiosInstance.patch(`/admin/warnings/${warningId}/revoke`);
        fetchWarnings();
        alert('Warnung erfolgreich aufgehoben');
        setShowConfirmModal(false);
      } catch (err) {
        console.error('Fehler beim Aufheben der Warnung:', err);
        alert('Fehler beim Aufheben der Warnung');
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWarningBadge = (type) => {
    return type === 'yellow' ? (
      <span className="badge badge-warning">Gelb</span>
    ) : (
      <span className="badge badge-error">Rot</span>
    );
  };

  const isActive = (warning) => {
    if (warning.revokedAt) return false;
    if (!warning.expiresAt) return true;
    return new Date(warning.expiresAt) > new Date();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Verwarnsystem</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowIssueModal(true)}
        >
          <FaPlus /> Warnung vergeben
        </button>
      </div>

      {/* Filter */}
      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h3 className="card-title">
            <FaFilter /> Filter
          </h3>
          <div className="flex gap-4">
            <div className="form-control">
              <label className="label">Typ</label>
              <select 
                className="select select-bordered"
                value={filter.type}
                onChange={(e) => setFilter({...filter, type: e.target.value})}
              >
                <option value="">Alle</option>
                <option value="yellow">Gelbe Karten</option>
                <option value="red">Rote Karten</option>
              </select>
            </div>
            <div className="form-control">
              <label className="label">Status</label>
              <select 
                className="select select-bordered"
                value={filter.active}
                onChange={(e) => setFilter({...filter, active: e.target.value})}
              >
                <option value="">Alle</option>
                <option value="true">Nur aktive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Warnungen-Tabelle */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : error ? (
            <div className="alert alert-error">
              <FaTimesCircle />
              <span>{error}</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Typ</th>
                      <th>Grund</th>
                      <th>Vergeben von</th>
                      <th>Vergeben am</th>
                      <th>Läuft ab</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warnings.map((warning) => (
                      <tr key={warning._id}>
                        <td className="font-semibold">
                          {warning.userId?.username || 'Unbekannt'}
                        </td>
                        <td>{getWarningBadge(warning.type)}</td>
                        <td className="max-w-xs truncate">
                          {warning.reason}
                        </td>
                        <td>{warning.issuedBy?.username || 'System'}</td>
                        <td>{formatDate(warning.issuedAt)}</td>
                        <td>
                          {warning.expiresAt ? (
                            <span className="flex items-center gap-1">
                              <FaClock />
                              {formatDate(warning.expiresAt)}
                            </span>
                          ) : (
                            <span className="text-gray-500">Unbefristet</span>
                          )}
                        </td>
                        <td>
                          {warning.revokedAt ? (
                            <span className="badge badge-ghost">Aufgehoben</span>
                          ) : isActive(warning) ? (
                            <span className="badge badge-success">Aktiv</span>
                          ) : (
                            <span className="badge badge-ghost">Abgelaufen</span>
                          )}
                        </td>
                        <td>
                          {!warning.revokedAt && isActive(warning) && (
                            <button 
                              className="btn btn-sm btn-error"
                              onClick={() => revokeWarning(warning._id)}
                            >
                              <FaUndo /> Aufheben
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                  <div className="btn-group">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`btn ${page === currentPage ? 'btn-active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal für neue Warnung */}
      {showIssueModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Warnung vergeben</h3>
            
            <div className="form-control w-full mt-4">
              <label className="label">User</label>
              <UserSearch
                selectedUser={selectedUser}
                onUserSelect={setSelectedUser}
                placeholder="Nach Username oder E-Mail suchen..."
              />
            </div>

            <div className="form-control w-full mt-4">
              <label className="label">Typ</label>
              <select 
                className="select select-bordered"
                value={newWarning.type}
                onChange={(e) => setNewWarning({...newWarning, type: e.target.value})}
              >
                <option value="yellow">Gelbe Karte</option>
                <option value="red">Rote Karte</option>
              </select>
            </div>

            <div className="form-control w-full mt-4">
              <label className="label">Grund</label>
              <textarea 
                className="textarea textarea-bordered"
                placeholder="Grund für die Warnung..."
                value={newWarning.reason}
                onChange={(e) => setNewWarning({...newWarning, reason: e.target.value})}
              />
            </div>

            <div className="form-control w-full mt-4">
              <label className="label">Läuft ab (optional)</label>
              <input 
                type="datetime-local"
                className="input input-bordered"
                value={newWarning.expiresAt}
                onChange={(e) => setNewWarning({...newWarning, expiresAt: e.target.value})}
              />
            </div>

            <div className="modal-action">
              <button 
                className="btn btn-primary"
                onClick={issueWarning}
              >
                Warnung vergeben
              </button>
              <button 
                className="btn"
                onClick={() => setShowIssueModal(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Warnung aufheben</h3>
            <p className="py-4">Sind Sie sicher, dass Sie diese Warnung aufheben möchten?</p>
            <div className="modal-action">
              <button 
                className="btn btn-error"
                onClick={confirmAction}
              >
                Ja, aufheben
              </button>
              <button 
                className="btn"
                onClick={() => setShowConfirmModal(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWarnings;
